/**
 * ASK transport — SSE stream of one composed analytics turn from the bridge
 * (POST /ask; nginx routes /ai/ → bridge with the prefix stripped).
 *
 * Events: status | text | view | chips | turn | done | error.
 * `seedViewData` is the BRIDGE→RENDERER seeding contract: it maps each
 * dataRef to the exact react-query key ViewRenderer uses, so a view card
 * mounts with warm cache and all in-card drilling stays client-side.
 * The map below and ViewRenderer's query keys must move together — see
 * .claude/skills/form-vocabulary (the add-a-form recipe).
 */
import type { QueryClient } from '@tanstack/react-query';
import { getToken } from './client';
import { getWorkspaceId } from './workspaces';
import { todayStr } from '../components/analytics/drillView';

const AI_BRIDGE_URL: string = import.meta.env.VITE_AI_BRIDGE_URL
  ?? (import.meta.env.DEV ? `http://${window.location.hostname}:5291` : '/ai');

const WORKSPACE_ID_KEY = 'a5core_workspace_id';

export interface AskDataset {
  dataRef: string;
  data: unknown;
}

/** Window CustomEvents the CopilotPanel (ask mode) fires at the analytics canvas. */
export const ASK_VIEW_EVENT = 'a5suite:ask-view';
export const ASK_TURN_EVENT = 'a5suite:ask-turn';

export interface AskViewEvent {
  view: { kind: string } & Record<string, unknown>;
  datasets: AskDataset[];
}

export interface AskSuggestion {
  label: string;
  prompt?: string;
  link?: string;
}

export type AskEvent =
  | { type: 'status'; stage: 'fetching' | 'composing' }
  | { type: 'text'; content: string }
  | AskViewEvent & { type: 'view' }
  | { type: 'chips'; suggestions: AskSuggestion[] }
  | { type: 'turn'; turn: unknown }
  | { type: 'done' }
  | { type: 'error'; content: string };

export interface AskOptions {
  message: string;
  conversationId?: string;
  onEvent: (event: AskEvent) => void;
  onConversationId?: (id: string) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export async function streamAsk(options: AskOptions): Promise<void> {
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;

  try {
    const response = await fetch(`${AI_BRIDGE_URL}/ask`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        message: options.message,
        conversationId: options.conversationId,
      }),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`AI Bridge error ${response.status}: ${body.slice(0, 200)}`);
    }

    const convId = response.headers.get('X-Conversation-Id');
    if (convId) options.onConversationId?.(convId);

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response stream');
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          options.onEvent(JSON.parse(line.slice(6)) as AskEvent);
        } catch {
          // Skip malformed JSON
        }
      }
    }
    options.onComplete?.();
  } catch (err) {
    options.onError?.(err instanceof Error ? err : new Error(String(err)));
  }
}

/**
 * Seed the react-query cache with the datasets a resolved view carries, so
 * ViewRenderer mounts warm and in-card drills are pure cache + client-side
 * filtering. dataRef → query key MUST mirror ViewRenderer's useQuery keys.
 */
export function seedViewData(queryClient: QueryClient, datasets: AskDataset[]): void {
  const wsId = getWorkspaceId();
  for (const { dataRef, data } of datasets) {
    const [base, arg] = dataRef.split(':');
    const argNum = Number(arg);
    switch (base) {
      case 'inbox':
        queryClient.setQueryData(['analytics-inbox', wsId], data);
        break;
      case 'overview':
        queryClient.setQueryData(['analytics-overview', wsId], data);
        break;
      case 'debtAging':
        queryClient.setQueryData(['debt-aging', arg, todayStr()], data);
        break;
      case 'debtReport':
        if (Number.isFinite(argNum)) queryClient.setQueryData(['debt-report', argNum], data);
        break;
      case 'customerInvoices':
        if (Number.isFinite(argNum)) queryClient.setQueryData(['customer-invoices', argNum], data);
        break;
      case 'invoice':
        if (Number.isFinite(argNum)) queryClient.setQueryData(['invoice', argNum], data);
        break;
      case 'journalEntry':
        if (Number.isFinite(argNum)) queryClient.setQueryData(['journal-entry', argNum], data);
        break;
      case 'matchingPayments':
        if (Number.isFinite(argNum)) queryClient.setQueryData(['matching-payments', argNum], data);
        break;
      case 'findings':
        // The findings view keys on the scanner filter as well as 'all'
        queryClient.setQueryData(['findings', 'all'], data);
        queryClient.setQueryData(['findings', undefined], data);
        break;
      default:
        break; // persons:<q> is model-only — never rendered
    }
  }
}
